import { Icon } from "../components/Icon";
import { ProgressBar } from "../components/ProgressBar";
import { Waves } from "../components/Waves";
import { useLanguage, useStore } from "../app/useStore";
import { formatTime } from "../format";

const SESSION_BARS = [28, 60, 95, 45, 78, 120, 84, 46, 90, 58, 28];

export function AudioPage({ itemId }: { itemId?: string }) {
  const ctx = useLanguage();
  const program = ctx.programs.find((x) => x.id === itemId && x.trackId === "audio");
  return program ? <AudioSession programId={program.id} /> : <AudioLibrary />;
}

function AudioLibrary() {
  const ctx = useLanguage();
  const { progress: p, programs, lessons } = ctx;
  const guided = programs.filter((x) => x.lessonId);
  const continuous = programs.filter((x) => ["listening-stories", "listening-words"].includes(x.id));
  const completed = (id: string) => p.tracks.audio.programs[id]?.completed || p.tracks.audio.programs[id]?.everCompleted;
  return (
    <>
      <div className="view-heading"><div>
        <p className="overline">Take your language along</p>
        <h1>Eyes off. Ears open.</h1>
        <p>Say it aloud with a guide, or let the language keep you company.</p>
      </div></div>
      <div className="section-title"><h2>Continuous listening</h2><span>Press play and settle in</span></div>
      <div className="listening-grid">
        {continuous.map((x) => {
          const stories = x.id === "listening-stories";
          return (
            <a key={x.id} className="listening-card" href={ctx.href("audio", x.id)}>
              <span className="listening-icon"><Icon name={stories ? "stories" : "audio"} /></span>
              <div>
                <h3>{stories ? "All stories" : "Words & phrases"}</h3>
                <p>{stories ? "Everyday moments, one after another." : "Your whole deck, heard in context."}</p>
                <span>{formatTime(x.duration)} of listening</span>
              </div>
              <span className="round-arrow"><Icon name="play" /></span>
            </a>
          );
        })}
      </div>
      <div className="section-title"><h2>Guided practice</h2><span>Listen. Say it. Hear it again.</span></div>
      <p className="section-description">Hear the words and phrases, then practice recalling them aloud. The pauses are your turn; no microphone needed.</p>
      <div className="lesson-list">
        {guided.map((x, i) => {
          const snapshot = p.tracks.audio.programs[x.id];
          const done = completed(x.id);
          return (
            <a key={x.id} className="lesson-row" href={ctx.href("audio", x.id)}>
              <span className="lesson-number">{done ? <Icon name="check" /> : String(i + 1).padStart(2, "0")}</span>
              <div className="lesson-body">
                <h3>{x.title}</h3>
                <p>{formatTime(x.duration)} · {lessons.find((l) => l.id === x.lessonId)?.wordIds.length || 0} familiar words and phrases</p>
                <ProgressBar done={snapshot?.position || 0} total={x.duration} label="Audio progress" />
              </div>
              <div className="lesson-meta"><strong>{done ? "Listen again" : snapshot?.position ? "Continue" : "Listen"} <Icon name="next" /></strong></div>
            </a>
          );
        })}
      </div>
      {!programs.length && <p className="empty-state">Audio lessons are being prepared. You can study and read stories in the meantime.</p>}
      <p className="footnote">Practice aloud for yourself. Listening completion measures exposure, not pronunciation or recall accuracy.</p>
    </>
  );
}

function AudioSession({ programId }: { programId: string }) {
  const ctx = useLanguage();
  const store = useStore();
  const { progress: p, player } = ctx;
  const program = ctx.programs.find((x) => x.id === programId)!;
  const lesson = ctx.lessons.find((l) => l.id === program.lessonId);
  const words = lesson ? ctx.deck.filter((w) => lesson.wordIds.includes(w.id)) : [];

  const ps = store.playerState;
  const active = ps.program?.id === program.id && ps.languageId === ctx.definition.id;
  const saved = p.tracks.audio.programs[program.id];
  const done = active ? ps.snapshot?.completed : saved?.completed;
  const playing = active && ps.playing;
  const cue = active ? program.cues.find((c) => ps.position >= c.start && ps.position < c.end) : null;

  const status = active && ps.error ? "Tap play to try again." : playing ? "You’re in good company." : done ? "A little more familiar." : saved?.position ? "Pick up where you paused." : "Ready when you are.";

  return (
    <>
      <a className="back-link" href={ctx.href("audio")}><Icon name="back" /> Audio</a>
      <div className="view-heading"><div>
        <p className="overline">{lesson ? "Guided practice" : "Continuous listening"}</p>
        <h1>{program.title}</h1>
        <p>{formatTime(program.duration)} · {lesson ? "Listen, repeat, and recall aloud." : "A complete session. No next button needed."}</p>
      </div></div>
      <section className="audio-session">
        <div className="audio-session-art" aria-hidden="true"><Icon name="audio" /><Waves bars={SESSION_BARS} /></div>
        <div className="audio-session-main">
          <span className="overline">Make this time yours</span>
          <h2 id="audio-session-status">{status}</h2>
          <p>{lesson
            ? "First, listen and repeat. Then answer the English prompts during the pauses, and compare your answer with the speaker."
            : "Put the screen away and let the words come to you. Your place is saved as you listen."}</p>
          <div className="button-row">
            <button className="button" id="program-play" onClick={() => { if (playing) player.pause(); else ctx.playProgram(program); }}>
              <Icon name={playing ? "pause" : "play"} />{playing ? " Pause" : done ? " Listen again" : saved?.position ? " Continue listening" : " Start listening"}
            </button>
            <button className="text-link" id="program-restart" onClick={() => ctx.playProgram(program, { restart: true })}><Icon name="repeat" /> Restart</button>
          </div>
          <p id="audio-cue" className="audio-cue" role="status">{cue?.kind === "pause" ? "Your turn. Say it aloud." : ""}</p>
        </div>
      </section>
      {lesson && (
        <>
          <div className="section-title">
            <h2>In this lesson</h2>
            <a className="text-link" href={ctx.href("study", lesson.id)}>Study these words <Icon name="arrow" /></a>
          </div>
          <div className="audio-word-list">
            {words.map((w) => (
              <button key={w.id} onClick={() => ctx.openWord(w.id)}><strong lang={ctx.definition.id}>{w.word}</strong><span>{w.meaning}</span></button>
            ))}
          </div>
        </>
      )}
      <p className="footnote">Start playback before locking your phone. Available lock-screen controls depend on your browser. Keep the tab open.</p>
    </>
  );
}
